import {
  Box,
  Menu,
  type MenuProps,
  type PopoverOrigin,
  type SxProps,
  type Theme,
} from '@mui/material';
import {
  Children,
  cloneElement,
  type ReactElement,
  type SyntheticEvent,
  useState,
} from 'react';

export type PopupMenuAnchor = ReactElement<{
  onClick?: (e: SyntheticEvent<HTMLElement>) => void;
}>;

interface PopupMenuProps {
  anchor: PopupMenuAnchor;
  children: PopupMenuAnchor | (PopupMenuAnchor | null)[];
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  sx?: SxProps<Theme>;
  onOpen?: () => void;
  onClose?: () => void;
  slotProps?: MenuProps['slotProps'];
}

export const PopupMenu = ({
  anchor,
  children,
  anchorOrigin,
  transformOrigin,
  sx,
  onOpen,
  onClose,
  slotProps,
}: PopupMenuProps) => {
  const [selectedMenu, setSelectedMenu] = useState<HTMLElement | null>(null);

  const handleClose = (e: SyntheticEvent<HTMLElement>) => {
    e.stopPropagation();
    setSelectedMenu(null);
  };

  return (
    <Box sx={sx}>
      {cloneElement(anchor, {
        onClick: (e: SyntheticEvent<HTMLElement>) => {
          e.stopPropagation();
          setSelectedMenu(e.currentTarget);
          anchor.props.onClick?.(e);
          onOpen?.();
        },
      })}
      <Menu
        anchorEl={selectedMenu}
        open={Boolean(selectedMenu)}
        onClose={(e) => {
          handleClose(e as SyntheticEvent<HTMLElement>);
          onClose?.();
        }}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        slotProps={slotProps}
      >
        {Children.map(children, (child) =>
          child
            ? cloneElement(child, {
                onClick: (e: SyntheticEvent<HTMLElement>) => {
                  handleClose(e);
                  child.props.onClick?.(e);
                },
              })
            : null,
        )}
      </Menu>
    </Box>
  );
};
